import React, { useEffect, useMemo, useRef, useState } from "react";

const RESEND_COOLDOWN_SEC = 50;

function VerifyMobileScreen({
  phoneNumber,
  onPhoneChange,
  onBack,
  onVerify,
  isSubmitting = false,
  onRequestOtp,
  onResendOtp,
  isRequestingOtp = false,
  eyebrowText = "Manage your requirement",
}) {
  const canVerify = /^[0-9]{10}$/.test(phoneNumber || "");
  const [mode, setMode] = useState("phone");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [isOtpFocused, setIsOtpFocused] = useState(false);
  const otpInputRef = useRef(null);
  const otpSubmittedRef = useRef(false);
  const [resendCooldownSeconds, setResendCooldownSeconds] = useState(0);

  const otpValue = useMemo(() => otp.join(""), [otp]);
  const canComplete = otpValue.length === 6;
  const activeOtpIndex = Math.min(otpValue.length, 5);

  useEffect(() => {
    if (mode !== "otp") return;
    otpSubmittedRef.current = false;
    otpInputRef.current?.focus();
  }, [mode]);

  useEffect(() => {
    if (mode !== "otp") return;
    if (!canComplete || isSubmitting) return;
    if (otpSubmittedRef.current) return;

    otpSubmittedRef.current = true;
    (async () => {
      try {
        await onVerify(otpValue);
      } catch {
        otpSubmittedRef.current = false;
        setOtp(["", "", "", "", "", ""]);
      }
    })();
  }, [canComplete, mode, onVerify, otpValue, isSubmitting]);

  useEffect(() => {
    if (mode === "otp" && onResendOtp) {
      setResendCooldownSeconds(RESEND_COOLDOWN_SEC);
    } else if (mode === "phone") {
      setResendCooldownSeconds(0);
    }
  }, [mode, onResendOtp]);

  useEffect(() => {
    if (resendCooldownSeconds <= 0) return undefined;
    const id = window.setInterval(() => {
      setResendCooldownSeconds((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [resendCooldownSeconds]);

  const handleOtpInputChange = (rawValue) => {
    const value = rawValue.replace(/\D/g, "").slice(0, 6);
    const next = value.split("");
    while (next.length < 6) next.push("");
    setOtp(next);
  };

  const handleVerifyClick = async () => {
    if (!canVerify) return;
    setOtp(["", "", "", "", "", ""]);
    if (onRequestOtp) {
    try {
      await onRequestOtp();
    } catch (error) {
      if (error?.skipVerifyAlert) return;
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Could not send OTP. Try again.";
      alert(message);
      return;
    }
    }
    setMode("otp");
  };

  const handleBackAction = () => {
    if (mode === "otp") {
      setMode("phone");
      return;
    }
    onBack();
  };

  const handleChangeNumber = () => {
    setMode("phone");
    otpSubmittedRef.current = false;
  };

  const handleResendOtp = async () => {
    if (
      !onResendOtp ||
      isRequestingOtp ||
      isSubmitting ||
      resendCooldownSeconds > 0
    ) {
      return;
    }
    try {
      setOtp(["", "", "", "", "", ""]);
      otpSubmittedRef.current = false;
      await onResendOtp();
      setResendCooldownSeconds(RESEND_COOLDOWN_SEC);
      window.setTimeout(() => otpInputRef.current?.focus(), 0);
    } catch (error) {
      if (error?.skipVerifyAlert) return;
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Could not resend OTP. Try again.";
      alert(message);
    }
  };

  const maskedPhone = `${phoneNumber || "98765"}`;

  return (
    <div className="req-screen req-screen-verify">
      <div className="req-content">
        <div className="req-inline-header">
          <button
            type="button"
            className="req-back-icon-btn"
            onClick={handleBackAction}
            aria-label="Go back"
          >
            <img src="/caretLeft.svg" alt="" />
          </button>
          <p className="req-eyebrow">{eyebrowText}</p>
        </div>

        {mode === "phone" ? (
          <>
            <h1 className="req-verify-title">Enter your mobile number</h1>
            <p className="req-verify-subtitle">
              We'll send you a one-time code to verify.
            </p>

            <div className="req-phone-input-wrap">
              <span className="req-country-code">+91 </span>
              <input
                type="tel"
                inputMode="numeric"
                maxLength={10}
                className="req-phone-input"
                value={phoneNumber}
                onChange={(e) => onPhoneChange(e.target.value.replace(/\D/g, ""))}
                placeholder="Phone number"
              />
            </div>
          </>
        ) : (
          <>
            <h1 className="req-verify-title">Verify your mobile number</h1>
            <p className="req-verify-subtitle">
              OTP sent to +91 {maskedPhone}{" "}
              <button
                type="button"
                className="req-change-number-btn"
                onClick={handleChangeNumber}
              >
                Change
              </button>
            </p>

            <div className="req-otp-wrap">
              <input
                ref={otpInputRef}
                type="tel"
                inputMode="numeric"
                maxLength={6}
                className="req-otp-hidden-input"
                value={otpValue}
                onChange={(e) => handleOtpInputChange(e.target.value)}
                onFocus={() => setIsOtpFocused(true)}
                onBlur={() => setIsOtpFocused(false)}
                aria-label="Enter 6 digit OTP"
              />
              {otp.map((digit, index) => (
                <div
                  key={index}
                  className={`req-otp-cell ${isOtpFocused && index === activeOtpIndex ? "is-active" : ""}`}
                >
                  {digit ? <span className="req-otp-digit">{digit}</span> : null}
                  {!digit && isOtpFocused && index === activeOtpIndex ? (
                    <span className="req-otp-caret" />
                  ) : null}
                  {!digit && !(isOtpFocused && index === activeOtpIndex) ? (
                    <span className="req-otp-dot" />
                  ) : null}
                </div>
              ))}
            </div>

            {onResendOtp ? (
              <div className="req-verify-resend-row">
                <button
                  type="button"
                  className="req-verify-resend-btn"
                  onClick={handleResendOtp}
                  disabled={
                    isRequestingOtp ||
                    isSubmitting ||
                    resendCooldownSeconds > 0
                  }
                >
                  {isRequestingOtp
                    ? "Resending..."
                    : resendCooldownSeconds > 0
                      ? `Resend OTP in ${resendCooldownSeconds}s`
                      : "Resend OTP"}
                </button>
              </div>
            ) : null}
          </>
        )}
      </div>

      <div className="req-footer">
        <div className="req-footer-actions req-footer-actions-select">
          <button type="button" className="req-secondary-btn" onClick={handleBackAction}>
            Go back
          </button>
          <button
            type="button"
            className="req-primary-btn"
            onClick={mode === "phone" ? handleVerifyClick : () => onVerify(otpValue)}
            disabled={
              mode === "phone"
                ? !canVerify || isRequestingOtp
                : !canComplete || isSubmitting
            }
          >
            {mode === "phone"
              ? isRequestingOtp
                ? "Sending code…"
                : "Verify"
              : "Complete"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default VerifyMobileScreen;
